import { Button, DatePicker, Form, Input, Select, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { SearchOutlined, UploadOutlined } from '@ant-design/icons';
import TableContent from 'src/components/CTable';
import { IListColumn, PaginationResponse } from 'src/@types/search-params';
import { useAppDispatch, useAppSelector } from 'src/redux/hook';
import { setModalState } from 'src/redux/slice/modal.store';
import useWindowSize from 'src/hooks/useWindowSize';
import {
  convertNumberByUnit,
  dataGet,
  exportExcel,
  formatDateTime,
  parseQueryString,
} from 'src/utils';
import { getAllAccountData } from 'src/api/account_data';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setGlobalState } from 'src/redux/slice/globalSlice';
import { ITransactionAccountData } from 'src/shared/interface/accountData.interface';
import { getOrderTypeString } from 'src/utils/accountData';
import { OrderType } from 'src/shared/enum/account_data';
import moment from 'moment';
import queryString from 'query-string';
import { getBalanceAccount } from 'src/api/localpay';
import { IBalanceLocalpay } from 'src/shared/interface/localpay.interface';
import { usePermission } from 'src/hooks/usePermission';

const options = [
  {
    label: 'KB',
    value: 'KB',
  },
  {
    label: 'MB',
    value: 'MB',
  },
  {
    label: 'GB',
    value: 'GB',
  },
  {
    label: 'TB',
    value: 'TB',
  },
];

const AccountData = () => {
  const [form] = Form.useForm();
  const size = useWindowSize();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [_, setSearchParams] = useSearchParams();
  const [unit, setUnit] = useState<string>('GB');
  const global = useAppSelector((state) => state.global);
  const [currentBalance, setCurrentBalance] = useState<string>('');
  const [data, setData] = useState<PaginationResponse<ITransactionAccountData[]>>();
  const [balanceInfo, setBalanceInfo] = useState<IBalanceLocalpay>();

  const { hasPermission, PERMISSIONS } = usePermission();

  const columns: IListColumn[] = useMemo(() => {
    return [
      { name: 'STT', fieldName: 'index', width: 20, isHideSort: true },
      {
        name: 'Mã đơn hàng',
        fieldName: 'paymentId',
        isHideSort: true,
        renderSpecialCard: (data: ITransactionAccountData) => (
          <button
            disabled={!hasPermission(PERMISSIONS.DATA_ACCOUNT.VIEW_ORDER_DETAIL)}
            className={`underline whitespace-nowrap cursor-pointer bg-transparent border-transparent ${
              hasPermission(PERMISSIONS.DATA_ACCOUNT.VIEW_ORDER_DETAIL)
                ? 'text-blue-400'
                : 'text-gray-400'
            } font-semibold`}
            onClick={() => {
              if (data.type === 'Datacode') {
                dispatch(
                  setGlobalState({
                    ...global,
                    openTabs: [
                      ...global.openTabs,
                      {
                        label: `Xem chi tiết Nạp data ${data.paymentId}`,
                        children: '',
                        key: `register-and-cancel-and-load-package/data-load/detail?id=${data.paymentId}`,
                      },
                    ],
                    activeKeyMenu: `register-and-cancel-and-load-package/data-load/detail?id=${data.paymentId}`,
                    order: data as any,
                  }),
                );
                navigate(
                  `/management/register-and-cancel-and-load-package/data-load/detail?id=${data.paymentId}`,
                );
              } else {
                dispatch(
                  setModalState({
                    open: true,
                    content: 'data-refill-order',
                    selectedHistory: data as any,
                    width: size.width <= 768 ? '95%' : '607',
                  }),
                );
              }
            }}
          >
            {data.paymentId ?? ''}
          </button>
        ),
      },
      {
        name: 'Loại đơn hàng',
        fieldName: 'type',
        isHideSort: true,
        renderSpecialCard: (data: ITransactionAccountData) => getOrderTypeString(data.type),
      },
      {
        name: 'Tổng dung lượng',
        fieldName: 'dataGb',
        isHideSort: true,
        renderSpecialCard: (data: ITransactionAccountData) =>
          `${
            data.type === OrderType.ADJUSTDECREASE || data.type === OrderType.DATACODE ? '-' : '+'
          } ${convertNumberByUnit(data.extraDataObject?.totalData ?? 0, 'KB', unit)}`,
      },
      {
        name: 'Đơn vị tính',
        fieldName: 'dataUnit',
        isHideSort: true,
        renderSpecialCard: () => unit,
      },
      {
        name: 'Số dư sau giao dịch',
        fieldName: 'balanceData',
        isHideSort: true,
        renderSpecialCard: (data: ITransactionAccountData) =>
          convertNumberByUnit(
            data.type === OrderType.TRANSFEROUT
              ? data.extraDataObject?.enterpriseBalanceData ?? 0
              : data.extraDataObject.balanceData ?? 0,
            'KB',
            unit,
          ),
      },
      { name: 'Trạng thái', fieldName: 'status', isHideSort: true },
      {
        name: 'Thời gian tạo',
        fieldName: 'createdAt',
        isHideSort: true,
        renderSpecialCard: (order: ITransactionAccountData) => {
          return <span>{order.createdAt ? formatDateTime(order.createdAt) : ''}</span>;
        },
      },
      { name: 'Người tạo', fieldName: 'createdBy', isHideSort: true },
    ];
  }, [data, unit]);

  const fetchAllAccountData = async (params = {}) => {
    const res = await getAllAccountData(params);
    if (res) {
      setData(res);
    }
  };

  const fetchBalance = async () => {
    const res = await getBalanceAccount();
    if (res) {
      setBalanceInfo(res);
      const value = convertNumberByUnit(res.data.balanceData ?? 0, 'KB', 'GB');
      setCurrentBalance(value);
    }
  };

  const handleSearch = (values: any) => {
    const { createdAt, search, type } = values;
    const params = {
      search: search.trim() || undefined,
      type: type || undefined,
      dateFrom:
        createdAt && values?.createdAt[0] ? values?.createdAt[0].format('YYYY-MM-DD') : undefined,
      dateTo:
        createdAt && values?.createdAt[1] ? values?.createdAt[1].format('YYYY-MM-DD') : undefined,
      pageIndex: 1,
      pageSize: 10,
    };
    setSearchParams(queryString.stringify(params));
    fetchAllAccountData(params);
  };

  const handleExport = () => {
    const params = location.search;
    const link = document.createElement('a');
    link.target = '_blank';
    link.download = `danh_sach_TKDATA_${moment().format('DDMMYYYYHHmmss')}.xlsx`;
    const paramsExport = params ? `${params}&DataUnit=${unit}` : `?DataUnit=${unit}`;
    exportExcel(
      process.env.REACT_APP_API_URL + '/api/SubscriberData/export-transaction-data' + paramsExport,
    ).then((res) => {
      link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
      link.click();
    });
  };

  useEffect(() => {
    fetchAllAccountData();
    fetchBalance();
    form.resetFields();
  }, [global.activeKeyId]);

  return (
    <>
      <Typography.Title level={5} className='mt-0'>
        Thông tin tài khoản data
      </Typography.Title>
      <div className='flex flex-col gap-4 mt-4 border border-solid border-[#BCC1CAFF] rounded-md py-[7px] px-3'>
        <div className='flex flex-col gap-2'>
          <Typography.Text>Mã tài khoản</Typography.Text>
          <Input
            readOnly
            value={balanceInfo && balanceInfo.payAccountData}
            className='max-w-[400px]'
          />
        </div>
        <div className='flex items-center gap-16 mb-2 max-md:flex-col max-md:gap-4 flex-wrap'>
          <div className='flex items-center gap-2'>
            <Typography.Text className='w-full max-w-fit'>Số dư hiện tại</Typography.Text>
            <Input readOnly value={currentBalance} className='max-w-[250px] font-normal text-4xl' />
            <Select
              showArrow
              showSearch
              defaultValue={'GB'}
              onChange={(ev) => {
                if (balanceInfo) {
                  const value = convertNumberByUnit(balanceInfo.data.balanceData ?? 0, 'KB', ev);
                  setCurrentBalance(value);
                }
              }}
              options={options}
              className='min-w-20'
            ></Select>
          </div>
          <div className='flex items-center'>
            <Typography.Text className='w-full'>Ngày tạo tài khoản</Typography.Text>
            <Input
              readOnly
              value={balanceInfo && formatDateTime(balanceInfo.createdAt ?? '')}
              className='min-w-[170px]'
            />
          </div>
        </div>
      </div>
      <Form className='flex justify-between flex-wrap mt-4' form={form} onFinish={handleSearch}>
        <Form.Item name='search' initialValue={''}>
          <Input prefix={<SearchOutlined />} placeholder='Mã đơn hàng' allowClear />
        </Form.Item>
        <Form.Item label='Loại đơn hàng' name='type' initialValue={''}>
          <Select
            showSearch
            showArrow
            defaultValue={''}
            options={[
              {
                label: 'Tất cả',
                value: '',
              },
              {
                label: 'Điều chỉnh giảm',
                value: OrderType.ADJUSTDECREASE,
              },
              {
                label: 'Điều chỉnh tăng',
                value: OrderType.ADJUSTINCREASE,
              },
              {
                label: 'Nhập data',
                value: OrderType.TRANSFEROUT,
              },
              {
                label: 'Nạp data',
                value: OrderType.DATACODE,
              },
            ]}
            filterOption={(input, option) => {
              return (option!.label as unknown as string)
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase());
            }}
            className='min-w-[200px]'
          ></Select>
        </Form.Item>
        <Form.Item name='createdAt' label='Ngày tạo' initialValue={''}>
          <DatePicker.RangePicker
            disabledDate={(current) => current && current >= moment().endOf('day')}
            placeholder={['Từ', 'Đến']}
            format={'DD/MM/YYYY'}
            size='middle'
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item label='Hiển thị số dư theo' initialValue={'GB'}>
          <Select
            showSearch
            defaultValue={'GB'}
            onChange={(ev) => {
              setUnit(ev);
            }}
            options={options}
            className='min-w-[140px]'
          ></Select>
        </Form.Item>
        <div className='flex gap-4 mb-4'>
          <Button
            type='primary'
            htmlType='submit'
            size='middle'
            className='w-full sm:w-auto order-1 sm:order-none'
          >
            Tìm kiếm
          </Button>
          {hasPermission(PERMISSIONS.DATA_ACCOUNT.EXPORT) && (
            <Button
              size='middle'
              className='w-full sm:w-auto order-1 sm:order-none'
              icon={<UploadOutlined />}
              onClick={handleExport}
            >
              Export
            </Button>
          )}
        </div>
      </Form>
      <TableContent
        bordered
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'Không tìm thấy kết quả' }}
        dataSource={dataGet(data, 'results', [])}
        totalRecord={dataGet(data, 'rowCount', 0)}
        onPageChange={(pageNumber, pageSize) => {
          const params = parseQueryString(location.search);
          fetchAllAccountData({
            ...params,
            pageIndex: pageNumber ?? 1,
            pageSize: pageSize ?? 10,
          });
        }}
        columns={columns}
      />
    </>
  );
};
export default AccountData;
